import { decimalToPercent } from '@calculations/decimal/percent';
import {
  eaches,
  perQuantityToSiteUnits,
  quantityToSiteUnits,
} from '@calculations/utilities/selling-uom';
import { Decimal } from '@snap-types/types/decimal';
import { priceStrategyUIConfig } from '@ui/config/price';
import { QuotePriceField } from '@ui/config/price/constants';
import { WritableQuoteBuilderStateType } from '@ui/features/quote-builder/types/state';

import {
  getQuoteLineOrThrow,
  getQuotePriceOrThrow,
} from '../../utilities/action-guards';
import { buildIssueId } from '../../utilities/issue-id';
import { deleteIssue } from '../../utilities/issue-managers';
import { validateContributionMargin } from '../../validations/contribution-margin';
import { validateGrossMargin } from '../../validations/gross-margin';
import { validateQuantity } from '../../validations/quantity';
import { validateUnitCost } from '../../validations/unit-cost';
import { validateUnitPrice } from '../../validations/unit-price';

type ReconcileQuotePriceFieldIssuesInput = {
  quoteLineUuid: string;
  quotePriceUuid: string;
  quantity?: Decimal | null;
  unitPrice?: Decimal | null;
  unitCost?: Decimal | null;
  grossMargin?: Decimal | null;
  contributionMargin?: Decimal | null;
  baseGrossMargin?: Decimal | null;
};

export function reconcileQuotePriceFieldIssues(
  state: WritableQuoteBuilderStateType,
  params: ReconcileQuotePriceFieldIssuesInput,
) {
  const { quoteLineUuid, quotePriceUuid } = params;
  const quotePrice = getQuotePriceOrThrow(state, quoteLineUuid, quotePriceUuid);

  /**
   * These are the same validators the entry handlers run on what the user
   * typed, so they judge precision against the site's selling unit — ADF's two
   * decimal places mean two per thousand. The values reaching this function are
   * freshly calculated and therefore in eaches, where that same price carries
   * three more places, so they have to be put back into the user's unit first.
   *
   * Without it a recalculated ADF unit price of 0.05 raises "cannot have more
   * than 2 decimal places" against a field the user never typed in, and no
   * value they can enter clears it.
   *
   * Margins are ratios and read the same in either basis, so they are absent —
   * as are service charges, whose quantity counts services rather than pieces.
   */
  const isServiceCharge = !!getQuoteLineOrThrow(state, quoteLineUuid)
    .serviceChargeId;

  const target = { sellingUnitOfMeasure: state.sellingUnitOfMeasure };

  const asEnteredQuantity = (value: Decimal) =>
    isServiceCharge ? value : quantityToSiteUnits(eaches(value), target);

  const asEnteredPerQuantity = (value: Decimal) =>
    isServiceCharge ? value : perQuantityToSiteUnits(eaches(value), target);

  const deleteFieldIssue = (field: string) => {
    deleteIssue(
      state,
      buildIssueId({
        area: 'quotePrice',
        quoteLineUuid,
        quotePriceUuid,
        type: 'invalid',
        field,
      }),
    );
  };

  const hasOwn = <K extends keyof ReconcileQuotePriceFieldIssuesInput>(
    key: K,
  ) => Object.prototype.hasOwnProperty.call(params, key);

  const strategySupportsField = (field: QuotePriceField) =>
    priceStrategyUIConfig[quotePrice.priceStrategy].fields.some(
      (fieldConfig) => fieldConfig.field === field,
    );

  if (hasOwn('quantity')) {
    if (params.quantity == null) {
      deleteFieldIssue('quantity');
    } else {
      validateQuantity(state, {
        quantity: asEnteredQuantity(params.quantity),
        quoteLineUuid,
        quotePriceUuid,
        isServiceCharge,
      });
    }
  }

  if (hasOwn('unitPrice')) {
    if (params.unitPrice == null) {
      deleteFieldIssue('unitPrice');
    } else {
      validateUnitPrice(state, {
        unitPrice: asEnteredPerQuantity(params.unitPrice),
        quoteLineUuid,
        quotePriceUuid,
      });
    }
  }

  if (hasOwn('unitCost')) {
    if (params.unitCost == null) {
      deleteFieldIssue('unitCost');
    } else {
      validateUnitCost(state, {
        unitCost: asEnteredPerQuantity(params.unitCost),
        quoteLineUuid,
        quotePriceUuid,
      });
    }
  }

  if (hasOwn('grossMargin')) {
    if (!strategySupportsField('grossMargin')) {
      deleteFieldIssue('grossMargin');
    } else if (params.grossMargin == null) {
      deleteFieldIssue('grossMargin');
    } else {
      validateGrossMargin(state, {
        grossMargin: decimalToPercent(params.grossMargin),
        quoteLineUuid,
        quotePriceUuid,
        field: 'grossMargin',
      });
    }
  }

  if (hasOwn('baseGrossMargin')) {
    if (!strategySupportsField('baseGrossMargin')) {
      deleteFieldIssue('baseGrossMargin');
    } else if (params.baseGrossMargin == null) {
      deleteFieldIssue('baseGrossMargin');
    } else {
      validateGrossMargin(state, {
        grossMargin: decimalToPercent(params.baseGrossMargin),
        quoteLineUuid,
        quotePriceUuid,
        field: 'baseGrossMargin',
      });
    }
  }

  if (hasOwn('contributionMargin')) {
    if (!strategySupportsField('contributionMargin')) {
      deleteFieldIssue('contributionMargin');
    } else if (params.contributionMargin == null) {
      deleteFieldIssue('contributionMargin');
    } else {
      validateContributionMargin(state, {
        contributionMargin: decimalToPercent(params.contributionMargin),
        quoteLineUuid,
        quotePriceUuid,
      });
    }
  }
}
